import { AGE_UP_THRESHOLD, effectiveAge } from "./age.js";
// Oinks ("OP" / Oink Points) — the fun-only currency earned from Learn & Earn practice and
// spent in the Oink Shop on cosmetics for Piggington (the pig) and the child's avatar.
//
// LAW 1 (the rule everything here is built around): Oinks are never money. No exchange rate,
// no conversion to tokens, no entry in the money transaction ledger, no parent adjust, not
// counted by the weekly spending cap. Visually: money is gold and circular (the Piggy Coin),
// savings are teal (the Vault), Oinks are PINK and the badge is a ROUNDED SQUARE — never a
// circle — so it can't read as a coin even though both feature a snout.
//
// This module is deliberately near-dependency-free (its ONLY import is lib/age.js, itself
// import-free, so no cycle is possible) so that Common.jsx can import
// from it without creating a cycle (Common ← utils ← [never oinks], oinks ← nothing).

/* ------------------------------ earning ------------------------------ */

// Per-session OP: +1 per correct answer, +5 for finishing a session at all (any score), +10
// on top for a perfect one (100% score, or a full-run TT streak). Paid ALWAYS — cooldown
// weeks, cap-blocked weeks and pure practice runs earn exactly the same OP as a paying week;
// that is the whole point of the feature. Awarded once, when a session finishes (quitting
// mid-test forfeits the run's OP — the finish bonus and the per-correct OP land together, so
// there's a single award point per session and no partial-credit bookkeeping).
export const OINK_CORRECT_OP = 1;
export const OINK_FINISH_BONUS = 5;
export const OINK_PERFECT_BONUS = 10;

// One formula shared by the award (in each game's finish effect) and the display (in each
// game's results screen) so the number a kid sees always equals the number they were paid.
export function oinkSessionOP(correctAnswers, perfect) {
  const correct = Math.max(0, correctAnswers | 0);
  const op = correct * OINK_CORRECT_OP + OINK_FINISH_BONUS + (perfect ? OINK_PERFECT_BONUS : 0);
  const parts = [
    `${correct} correct +${correct * OINK_CORRECT_OP}`,
    `finished +${OINK_FINISH_BONUS}`,
  ];
  if (perfect) parts.push(`perfect +${OINK_PERFECT_BONUS}`);
  return { op, detail: parts.join(" · ") };
}

/* ------------------------------ the pig ------------------------------ */

// The mascot's name scales with age, at the unified AGE_UP_THRESHOLD (8) shared with the
// kid theme, the arcade's Gentle mode, and the hero strip's wealth label (Aug 2026
// consolidation — the name previously split at 7 while colours split at 9): under-8s get
// the softer "Piggles", 8-and-up get the drier "Piggington". Missing/non-numeric age gets
// Piggington, the safe default for an unknown (likely older) child. Spelling note:
// PIGGINGTON, double g.
export function pigDisplayName(age) {
  return typeof age === "number" && age < AGE_UP_THRESHOLD ? "Piggles" : "Piggington";
}

// R4 rename option (2026-08): a child can give their own pig a name, stored per-profile as
// child.pigName. Empty/whitespace-only stays unset (falls back to the age-based default
// below) rather than storing a blank override. Household-local — no uniqueness check across
// children, no server round-trip beyond the normal state save.
export const PIG_NAME_MAX_LENGTH = 16;

export function pigDisplayNameFor(child) {
  const custom = child && typeof child.pigName === "string" ? child.pigName.trim() : "";
  return custom || pigDisplayName(effectiveAge(child));
}

// One friendly line a day on the pig card — deterministic per child per day (no flicker on
// re-render, different kids can get different lines on the same day). The caller passes the
// seed string (e.g. `${todayStr()}-${child.id}`) so this module stays import-free.
export const PIG_SPEECH_LINES = [
  "Oink. Ready for a brain boost?",
  "Sniff sniff… I smell Oinks to earn.",
  "Every correct answer earns an Oink.",
  "A perfect test earns bonus Oinks — go on.",
  "New hat day? Check the Oink Shop.",
  "Practice never stops paying Oinks.",
];

export function pigLineFor(seedStr) {
  let hash = 0;
  const s = String(seedStr || "");
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) | 0;
  return PIG_SPEECH_LINES[Math.abs(hash) % PIG_SPEECH_LINES.length];
}

/* ------------------------------ the shop ------------------------------ */

// One equipped item per slot. Multiple slots can be worn simultaneously (a kid can have a
// bowler hat AND glasses AND a tee all on at once) — the conflict rule is per-slot only,
// e.g. exactly one HEAD item at a time (bowler xor party hat xor baseball cap xor crown xor
// wooly hat xor hair), enforced structurally by cosmetics.equipped being one id per slot,
// never a list. ONE cross-slot exception, from the Aug 2026 wardrobe-expansion handoff:
// "outfit" (pyjamas — a one-piece covering torso + legs) excludes "torso" and vice versa;
// applyEquip below enforces it, so no caller can produce pyjamas-plus-coat by accident.
// "celebration" swaps the confetti set used by Learn & Earn results celebrations only —
// money celebrations (approvals, vault moves) deliberately keep their coin imagery, so money
// moments always look like money (Law 1 again). "frame" is a ring around the AVATAR, not
// worn by Piggington — both are separate, unrelated mechanics from the body slots below.
//
// Aug 2026 wardrobe expansion: these are now shop TABS, not 1:1 slots — each tab lists one
// or more slots (`slots`). Twelve one-slot tabs (several holding a single item) made the
// pill row unusably long on a phone, so related slots share a tab: Face = brows + lashes,
// Clothes = torso + outfit (pyjamas sits naturally beside the coat/jumper it excludes).
// Equipping still keys off each ITEM's own slot — the tab grouping is purely presentational.
// Tab labels are asserted by tests/specs/12-oinks.spec.js — rename in both places.
//
// Aug 2026 characters/economy task: "Friends" goes FIRST — characters are the headline, the
// shop opens on this tab (oinks-pricing-strategy.md §5, task brief step 4). `slots: []`
// deliberately, unlike every other tab: characters aren't OINK_CATALOG items with a wardrobe
// slot, they're a separate catalog (CHARACTER_CATALOG) with their own ownership/equip state.
// The shop component special-cases this tab id to source from that catalog instead of
// filtering OINK_CATALOG by `slots`.
export const OINK_SLOTS = [
  { id: "characters", label: "Friends", emoji: "🐾", slots: [] },
  { id: "head", label: "Head", emoji: "🎩", slots: ["head"] },
  { id: "eyes", label: "Eyes", emoji: "🕶️", slots: ["eyes"] },
  { id: "face", label: "Face", emoji: "✨", slots: ["brows", "lashes"] },
  { id: "ears", label: "Ears", emoji: "👂", slots: ["ears"] },
  { id: "neck", label: "Neck", emoji: "👔", slots: ["neck"] },
  { id: "clothes", label: "Clothes", emoji: "👕", slots: ["torso", "outfit", "legs"] },
  { id: "feet", label: "Feet", emoji: "👟", slots: ["feet"] },
  // "held" grouped into the same tab as "hands" (bugfix, Aug 2026): they're a natural pair
  // in the shop (a coin/gloves vs. an umbrella/balloon in the paw) and the manifest itself
  // says they're meant to be worn together — see PIGGINGTON_SLOT_RULES.held in
  // lib/piggington.js. Each item's own `slot` still drives its OWN equip conflicts.
  { id: "hands", label: "Hands", emoji: "🪙", slots: ["hands", "held"] },
  { id: "frame", label: "Frames", emoji: "🖼️", slots: ["frame"] },
  { id: "celebration", label: "Parties", emoji: "🎉", slots: ["celebration"] },
];

// Static catalog, same pattern as STARTER_VOCAB_LISTS: only OWNED ids ever reach KV, the
// catalog itself ships in code. Prices are tuned to roughly 2–8 typical sessions each
// (a typical session ≈ 15 OP, a perfect 12-question one = 27).
//
// 2026-08: the old single "hat" catalog (hat_party/hat_cap/hat_wizard/hat_crown) is retired
// — replaced by the real vector sprite's slot wardrobe (see lib/piggington.js and
// public/piggington-sprite.svg). cos-baseball-cap and cos-crown are head items added
// alongside the sprite's handoff set, matching its established geometry. Ids match the
// sprite's symbol ids exactly (findOinkItem/equippedCosmetic key off these directly, no
// translation layer) — do not rename an id here without renaming the matching <symbol> too.
//
// Aug 2026 wardrobe expansion: 12 new handoff items across four new slots (brows, lashes,
// ears, outfit) plus new head/torso/feet options. Prices for the new items follow the same
// 2–8-session tuning: small accents (socks, brows, lashes) at the cheap end, everyday
// clothes mid-range, the big statement pieces (headphones, pyjamas) near the crown's end of
// the scale. Emoji are the tab/preview fallbacks only — wardrobe items render the REAL
// sprite in the shop, so the emoji rarely shows for them.
//
// Aug 2026 characters/economy task: 11 more handoff items (oinks-pricing-strategy.md §3),
// priced into the existing bands — no existing item's price changes.
//
// BUGFIX (Aug 2026, follow-up): the original version of this batch catalogued cos-skirt as
// slot "torso" and the four cos-held-* items as slot "hands", simplifying away the handoff
// manifest's real "legs" and "held" slots because this catalog didn't have them yet. That
// silently made a "held" item mutually exclusive with a "hands" item (wrong — the manifest
// is explicit they layer together, e.g. a coin in one paw plus gloves) and cos-skirt
// exclusive with every torso garment instead of layering over one. Slot values below now
// match docs/piggington-manifest.json verbatim — "legs" and "held" are real slots (see
// PIGGINGTON_WARDROBE_SLOTS and OINK_SLOTS below, and applyEquip's exclusion rules).
export const OINK_CATALOG = [
  { id: "cos-socks", slot: "feet", label: "Socks", emoji: "🧦", price: 15 },
  { id: "cos-shoes", slot: "feet", label: "Shoes", emoji: "👟", price: 20 },
  { id: "cos-slippers", slot: "feet", label: "Slippers", emoji: "🥿", price: 20 },
  { id: "cos-tie", slot: "neck", label: "Tie", emoji: "👔", price: 20 },
  { id: "cos-bow-tie", slot: "neck", label: "Bow Tie", emoji: "🎀", price: 20 },
  { id: "cos-eyebrows", slot: "brows", label: "Eyebrows", emoji: "🤨", price: 20 },
  { id: "cos-eyelashes", slot: "lashes", label: "Eyelashes", emoji: "✨", price: 20 },
  { id: "cos-tee", slot: "torso", label: "T-Shirt", emoji: "👕", price: 25 },
  { id: "cos-shorts", slot: "torso", label: "Shorts", emoji: "🩳", price: 25 },
  { id: "cos-gloves", slot: "hands", label: "Gloves", emoji: "🧤", price: 25 },
  { id: "cos-mittens", slot: "hands", label: "Mittens", emoji: "❄️", price: 25 },
  { id: "cos-skirt", slot: "legs", label: "Skirt", emoji: "👚", price: 25 },
  { id: "cos-glasses", slot: "eyes", label: "Glasses", emoji: "👓", price: 30 },
  { id: "cos-hair", slot: "head", label: "Hair", emoji: "💇", price: 30 },
  { id: "cos-jumper", slot: "torso", label: "Jumper", emoji: "🧶", price: 30 },
  { id: "cos-ear-muffs", slot: "ears", label: "Ear Muffs", emoji: "🧣", price: 30 },
  { id: "cos-held-balloon", slot: "held", label: "Balloon", emoji: "🎈", price: 30 },
  { id: "cos-hair-long-blonde", slot: "head", label: "Long Hair, Blonde", emoji: "👱", price: 30 },
  { id: "cos-hair-long-brown", slot: "head", label: "Long Hair, Brown", emoji: "🧑", price: 30 },
  { id: "cos-monocle", slot: "eyes", label: "Monocle", emoji: "🧐", price: 35 },
  { id: "cos-held-umbrella", slot: "held", label: "Umbrella", emoji: "☂️", price: 35 },
  { id: "cos-held-book", slot: "held", label: "Book", emoji: "📖", price: 35 },
  { id: "cos-coin", slot: "hands", label: "Lucky Coin", emoji: "🪙", price: 40 },
  { id: "cos-wooly-hat", slot: "head", label: "Wooly Hat", emoji: "⛄", price: 40 },
  { id: "cos-bowler", slot: "head", label: "Bowler Hat", emoji: "🎩", price: 45 },
  { id: "cos-party-hat", slot: "head", label: "Party Hat", emoji: "🥳", price: 45 },
  { id: "cos-baseball-cap", slot: "head", label: "Baseball Cap", emoji: "🧢", price: 45 },
  { id: "cos-dress", slot: "outfit", label: "Dress", emoji: "👗", price: 45 },
  { id: "cos-mohawk", slot: "head", label: "Mohawk", emoji: "🧑‍🎤", price: 45 },
  { id: "cos-coat", slot: "torso", label: "Coat", emoji: "🧥", price: 50 },
  { id: "cos-held-bank", slot: "held", label: "Piggy Bank", emoji: "🐷", price: 50 },
  { id: "cos-headphones", slot: "ears", label: "Headphones", emoji: "🎧", price: 55 },
  { id: "cos-pyjamas", slot: "outfit", label: "Pyjamas", emoji: "🌙", price: 60 },
  { id: "cos-crown", slot: "head", label: "Crown", emoji: "👑", price: 90 },
  { id: "frame_rainbow", slot: "frame", label: "Rainbow Frame", emoji: "🌈", price: 80 },
  { id: "frame_stars", slot: "frame", label: "Star Frame", emoji: "⭐", price: 100 },
  { id: "frame_flames", slot: "frame", label: "Flame Frame", emoji: "🔥", price: 120 },
  { id: "party_rainbow", slot: "celebration", label: "Rainbow Party", emoji: "🎊", price: 60 },
  { id: "party_space", slot: "celebration", label: "Space Party", emoji: "🚀", price: 90 },
  { id: "party_dino", slot: "celebration", label: "Dino Party", emoji: "🦕", price: 90 },
];

export function findOinkItem(id) {
  return OINK_CATALOG.find((i) => i.id === id) || null;
}

/* ------------------------------ characters ------------------------------ */

// Aug 2026 characters/economy task: the top of the Oinks ladder, priced ABOVE every cosmetic
// (>120) so they're unambiguously the prestige tier (oinks-pricing-strategy.md §4) — four
// tiers, each a visible next rung. Piggington (pg-base) is NOT in this catalog: he's free,
// default, always owned, never purchasable, never removable — represented everywhere here as
// equippedCharacter() returning null, not as an entry with price 0. Ids match the sprite's
// symbol ids exactly, same rule as OINK_CATALOG above.
// LB-168/169 (Sep 2026, Alan): a child's identity is a character. Four STARTERS are free and
// always owned — Piggington (pg-base, still not a catalog entry) plus Bear, Rabbit and Dog —
// picked at setup. The Friends tier at 200 is refilled by Cat, Frog and Duck so the first
// buy stays the rung that makes the ladder work (weeks 5–7 at a typical 30–45 Oinks/week).
export const STARTER_CHARACTER_IDS = ["pg-base", "ch-bear", "ch-rabbit", "ch-dog", "ch-boy", "ch-girl", "ch-dinosaur", "ch-monster"];
export function isStarterCharacter(id) { return STARTER_CHARACTER_IDS.includes(id || "pg-base"); }
// LB-327 (Alan, 17 Sep 2026): Boy, Girl, Dinosaur and Monster join the starters (free for every
// kid, new and existing — ownership is computed in ownedCharacters(), nothing is written to a
// profile). The Naked Mole Rat is the top price, swapped with the Unicorn (the ladder keeps its
// shape); the other six new characters are spread across the existing tiers, one per tier
// before any tier gets a second, cheapest to dearest: Pirate, Alien, Knight, Astronaut, Robot,
// Dragon; Alan then moved the Dragon up to Legendary 650. Prices are code, not published content. Display names come from the CMS
// (character.<id>.name, see characterName() in components/Common.jsx); `label` is the seed value.
// Order below is tier order — the shop draws a tier heading wherever `tier` changes.
export const CHARACTER_CATALOG = [
  { id: "ch-bear", label: "Bear", tier: "Starter", price: 0, starter: true },
  { id: "ch-rabbit", label: "Rabbit", tier: "Starter", price: 0, starter: true },
  { id: "ch-dog", label: "Dog", tier: "Starter", price: 0, starter: true },
  { id: "ch-boy", label: "Boy", tier: "Starter", price: 0, starter: true },
  { id: "ch-girl", label: "Girl", tier: "Starter", price: 0, starter: true },
  { id: "ch-dinosaur", label: "Dinosaur", tier: "Starter", price: 0, starter: true },
  { id: "ch-monster", label: "Monster", tier: "Starter", price: 0, starter: true },
  { id: "ch-cat", label: "Cat", tier: "Friends", price: 200 },
  { id: "ch-frog", label: "Frog", tier: "Friends", price: 200 },
  { id: "ch-duck", label: "Duck", tier: "Friends", price: 200 },
  { id: "ch-pirate", label: "Pirate", tier: "Friends", price: 200 },
  { id: "ch-robot", label: "Robot", tier: "Friends", price: 200 },
  { id: "ch-fox", label: "Fox", tier: "Explorers", price: 300 },
  { id: "ch-squirrel", label: "Squirrel", tier: "Explorers", price: 300 },
  { id: "ch-penguin", label: "Penguin", tier: "Explorers", price: 300 },
  { id: "ch-owl", label: "Owl", tier: "Explorers", price: 300 },
  { id: "ch-alien", label: "Alien", tier: "Explorers", price: 300 },
  { id: "ch-raccoon", label: "Raccoon", tier: "Rare", price: 450 },
  { id: "ch-wolf", label: "Wolf", tier: "Rare", price: 450 },
  { id: "ch-gorilla", label: "Gorilla", tier: "Rare", price: 450 },
  { id: "ch-knight", label: "Knight", tier: "Rare", price: 450 },
  { id: "ch-unicorn", label: "Unicorn", tier: "Legendary", price: 650 },
  { id: "ch-astronaut", label: "Astronaut", tier: "Legendary", price: 650 },
  { id: "ch-dragon", label: "Dragon", tier: "Legendary", price: 650 }, // LB-328 (Alan, 17 Sep): moved up from Explorers 300
  { id: "ch-molerat", label: "Naked Mole Rat", tier: "Legendary", price: 750 },
];

export function findCharacterItem(id) {
  return CHARACTER_CATALOG.find((c) => c.id === id) || null;
}

export function ownedCharacters(child) {
  const owned = (child && child.characters && Array.isArray(child.characters.owned)) ? child.characters.owned : [];
  // Starters are always owned (LB-168) — never stored, never purchasable, never removable.
  return Array.from(new Set([...STARTER_CHARACTER_IDS.filter((id) => id !== "pg-base"), ...owned]));
}

/* ------------------------------ identity (LB-168) ------------------------------ */

// A child has chosen a character (at setup, when added later, or from the avatar sheet). Until
// then `ChildMark` keeps rendering their emoji — the emoji stays until they act (Alan, 7 Sep).
export function hasChosenCharacter(child) {
  return !!(child && child.identityChosen);
}

// Oinks earned per week at this child's own pace: last 28 days ÷ 4, floor 10, so a new child
// never sees "never". Reads the Oink ledger the shop already keeps (oinkLedger entries with a
// positive `op`); falls back to the floor when there is no ledger yet.
export function recordOinkEarn(child, op) {
  if (!child || !(op > 0)) return;
  if (!Array.isArray(child.oinkLedger)) child.oinkLedger = [];
  child.oinkLedger.push({ at: new Date().toISOString(), op });
  if (child.oinkLedger.length > 60) child.oinkLedger.splice(0, child.oinkLedger.length - 60);
}

export function oinkPacePerWeek(child) {
  const ledger = (child && Array.isArray(child.oinkLedger)) ? child.oinkLedger : [];
  const since = Date.now() - 28 * 86400000;
  const earned = ledger.filter((e) => e && e.op > 0 && Date.parse(e.at || e.timestamp || 0) >= since).reduce((a, e) => a + e.op, 0);
  return Math.max(10, Math.round(earned / 4));
}

// "62 Oinks to go · about 2 weeks at your pace" — the distance to a locked item. Returns
// { toGo, weeks } or null when the item is affordable now.
export function oinksToGo(child, price) {
  const have = childOinks(child);
  const toGo = Math.max(0, (price || 0) - have);
  if (toGo === 0) return null;
  return { toGo, weeks: Math.max(1, Math.ceil(toGo / oinkPacePerWeek(child))) };
}

// The cheapest character this child doesn't own yet — what "Next:" on Home points at.
export function nextCharacterFor(child) {
  const owned = new Set(ownedCharacters(child));
  return CHARACTER_CATALOG.filter((c) => !c.starter && !owned.has(c.id)).sort((a, b) => a.price - b.price)[0] || null;
}

// null = Piggington (pg-base) — the always-available default, never stored in `owned`.
export function equippedCharacter(child) {
  return (child && child.characters && child.characters.equipped) || null;
}

// The actual sprite <symbol> id to render as the body base — what Piggington/SnackCatch pass
// to <Piggington base={...}>. Same null-means-default convention as equippedCharacter, just
// resolved to the concrete id every render call site actually needs.
export function equippedCharacterBaseId(child) {
  return equippedCharacter(child) || "pg-base";
}

// Confetti emoji sets for the "Parties" cosmetics — consumed by Celebration's emojiOverride
// prop on Learn & Earn results screens (see Tests.jsx).
// PARTY_EMOJI_SETS retired (LB-162, Sep 2026): the three party skins now render as sprite
// pieces — see PARTY_PIECE_SETS in components/Common.jsx. The catalog entries above are the
// purchasable items; this file no longer carries their art.

// Ring styles for the "Frames" cosmetics — consumed by Avatar (Common.jsx), which reads the
// equipped frame straight off the profile so no call site needs to change.
export const AVATAR_FRAME_STYLES = {
  frame_rainbow: { ring: "conic-gradient(#ef4444, #f97316, #fbbf24, #22c55e, #3b82f6, #8b5cf6, #ef4444)" },
  // `badge` is a sprite id + tone since LB-162 (rendered by Avatar in Common.jsx).
  frame_stars: { ring: "#8b5cf6", badge: "ic-star", badgeColor: "#E0A81C" },
  frame_flames: { ring: "linear-gradient(135deg, #f97316, #ef4444)", badge: "ic-flame", badgeColor: "#D97706" },
};

// Safe accessors — profiles created before Oinks existed (and preview-mode profiles, which
// never pass through migrateState) have neither field, so every read must tolerate absence.
export function childOinks(child) {
  return (child && typeof child.oinks === "number" ? child.oinks : 0);
}

export function equippedCosmetic(child, slot) {
  return (child && child.cosmetics && child.cosmetics.equipped && child.cosmetics.equipped[slot]) || null;
}

export function ownedCosmetics(child) {
  return (child && child.cosmetics && Array.isArray(child.cosmetics.owned)) ? child.cosmetics.owned : [];
}

// The body-wardrobe slots (as opposed to frame/celebration, which aren't worn by
// Piggington at all — see OINK_SLOTS above). Every non-null value here is safe to pass
// straight into <Piggington cosmetics={...}> — item ids match sprite symbol ids exactly.
// Aug 2026 wardrobe expansion: six slots became ten (brows, lashes, ears, outfit added).
// Aug 2026 characters/economy bugfix: ten became twelve (legs, held added) — both were
// missing entirely, which is why every legs/held item silently failed to render (see
// lib/piggington.js's header comment on this same bug).
export const PIGGINGTON_WARDROBE_SLOTS = ["head", "eyes", "brows", "lashes", "ears", "neck", "torso", "outfit", "legs", "feet", "hands", "held"];

// Every equippable slot including the two non-body ones — the canonical key list for the
// cosmetics.equipped object (ensureCosmetics backfills any that are missing).
export const ALL_EQUIP_SLOTS = [...PIGGINGTON_WARDROBE_SLOTS, "frame", "celebration"];

export function wornPiggingtonCosmetics(child) {
  return PIGGINGTON_WARDROBE_SLOTS.map((slot) => equippedCosmetic(child, slot)).filter(Boolean);
}

// Write-time initialiser shared by every updater that touches Oink state, for the same
// "fields may be absent" reason as the accessors above. 2026-08: default equipped slots
// updated from the old single "hat" to the sprite's body slots — an existing household
// that already has an equipped object keeps its values, with any MISSING slot keys
// backfilled to null (Aug 2026 wardrobe expansion added brows/lashes/ears/outfit, so every
// pre-expansion household is missing exactly those four; backfilling here beats scattering
// `|| null` guards at write sites). Any lingering old "hat" key on real data is a harmless,
// unused orphan rather than something this needs to actively migrate.
//
// Aug 2026 characters/economy task: same treatment for `characters` — a profile from before
// characters existed has no such field at all, so it's backfilled to { owned: [], equipped:
// null } (null = Piggington, the always-available default) rather than every character call
// site needing its own absence guard.
export function ensureCosmetics(profile) {
  if (typeof profile.oinks !== "number") profile.oinks = 0;
  if (!profile.cosmetics || typeof profile.cosmetics !== "object") profile.cosmetics = {};
  if (!Array.isArray(profile.cosmetics.owned)) profile.cosmetics.owned = [];
  if (!profile.cosmetics.equipped || typeof profile.cosmetics.equipped !== "object") profile.cosmetics.equipped = {};
  for (const slot of ALL_EQUIP_SLOTS) {
    if (!(slot in profile.cosmetics.equipped)) profile.cosmetics.equipped[slot] = null;
  }
  if (!profile.characters || typeof profile.characters !== "object") profile.characters = {};
  if (!Array.isArray(profile.characters.owned)) profile.characters.owned = [];
  if (!("equipped" in profile.characters)) profile.characters.equipped = null;
  return profile;
}

// The one write path for equipping (App.jsx's buy auto-equip AND its wear/take-off handler
// both call this) — exists so the outfit/torso/legs exclusion lives in exactly one place.
// Pyjamas and dresses are one-piece, covering torso + legs (Aug 2026 handoff, dress added by
// the characters/economy task): wearing one takes the torso AND legs items off; wearing a
// torso or legs item takes the outfit off. Bugfix, same task: this previously only crossed
// outfit<->torso, never touching legs (cos-skirt/"legs" didn't exist yet), so a skirt worn
// under a dress — or a dress worn over a skirt — could sit there uncleared, contradicting
// PIGGINGTON_SLOT_RULES.outfit ("excludes torso and legs items"). Taking something OFF
// (itemId null) never touches the other slots. Callers are responsible for the ownership
// check (App.jsx already does it) — this only handles slot mechanics.
export function applyEquip(profile, slot, itemId) {
  ensureCosmetics(profile);
  profile.cosmetics.equipped[slot] = itemId;
  if (itemId) {
    if (slot === "outfit") { profile.cosmetics.equipped.torso = null; profile.cosmetics.equipped.legs = null; }
    if (slot === "torso" || slot === "legs") profile.cosmetics.equipped.outfit = null;
  }
  return profile;
}


// ---- LB-247 Gift Oinks, child → sibling (Alan, 9–10 Sep 2026) ----
// A TRANSFER, never a gift-on-top: the sender loses exactly what the receiver gains, so
// Oinks are still only ever created by learning (Law 1) — the parent lever (LB-168) is the
// separate, deliberate exception. Guardrails: minimum 5, never below zero, never to yourself,
// one gift per sibling per day. No parent approval — Oinks are never money. The receiver
// gets the celebration next time they open Kid Mode (pendingOinkGift on their profile);
// the household feed gets one row.
export const OINK_GIFT_MIN = 5;
export const OINK_GIFT_PRESETS = [10, 25, 50];
export function oinkGiftCheck(sender, receiver, amount, today) {
  if (!sender || !receiver) return { ok: false, reason: "no_child" };
  if (sender.id === receiver.id) return { ok: false, reason: "self" };
  const n = Math.floor(Number(amount) || 0);
  if (n < OINK_GIFT_MIN) return { ok: false, reason: "min" };
  if (n > childOinks(sender)) return { ok: false, reason: "balance" };
  if (sender.oinkGifts && sender.oinkGifts[receiver.id] === today) return { ok: false, reason: "today" };
  return { ok: true, amount: n };
}
export function giftOinks(s, fromId, toId, amount, today, nowIso) {
  const from = s.profiles.find((p) => p.id === fromId);
  const to = s.profiles.find((p) => p.id === toId);
  const check = oinkGiftCheck(from, to, amount, today);
  if (!check.ok) return check;
  ensureCosmetics(from); ensureCosmetics(to);
  from.oinks -= check.amount;
  to.oinks += check.amount;
  from.oinkGifts = { ...(from.oinkGifts || {}), [toId]: today };
  to.pendingOinkGift = { from: from.name, amount: check.amount, at: nowIso };
  s.transactions.unshift({ id: Math.random().toString(36).slice(2, 12), childId: toId, amount: check.amount, reason: `${from.name} gave ${to.name} ${check.amount} Oinks`, type: "credit", balance: "oinks", kind: "oink_gift_sibling", fromChildId: fromId, timestamp: nowIso });
  return { ok: true, amount: check.amount };
}

// ---- LB-356: undoing a gift of Oinks (Alan, 21 Sep) ----
// "Undoing a grown-up's gift takes the Oinks back, never below zero; undoing a sibling gift
// returns them to the sender." Both halves obey Law 1: an undo never CREATES Oinks. Only what
// the receiver still holds can come back, so a gift undone after the receiver spent some of it
// returns that much and no more, and a sibling's gift sends exactly that amount home.
//
// oinkGiftTakeBack() answers "how many would move" without changing anything, so the caller
// can say "nothing to take back" instead of writing an empty reversal. reverseOinkGift()
// MUTATES the state passed in (call only inside update()) and returns the Oinks moved, or null
// when the row is not an undoable gift. Only the two gift kinds qualify: they are the only
// Oinks rows the ledger holds (Oinks earned by learning are never written to it).
export function isUndoableOinkGift(row) {
  return !!row && row.balance === "oinks" && row.type === "credit" && !row.reversed && !row.reversalOf
    && (row.kind === "oink_gift" || row.kind === "oink_gift_sibling");
}
export function oinkGiftTakeBack(s, row) {
  if (!isUndoableOinkGift(row)) return null;
  const to = (s.profiles || []).find((p) => p.id === row.childId);
  if (!to) return null;
  return Math.max(0, Math.min(Math.floor(row.amount || 0), childOinks(to)));
}
export function reverseOinkGift(s, row, { newId, deviceId, nowIso }) {
  const taken = oinkGiftTakeBack(s, row);
  if (taken === null || taken === 0) return taken;
  const to = s.profiles.find((p) => p.id === row.childId);
  ensureCosmetics(to);
  to.oinks -= taken;
  const sibling = row.kind === "oink_gift_sibling";
  const sender = sibling ? s.profiles.find((p) => p.id === row.fromChildId) : null;
  // The receiver's moment for this gift, if they have not opened Kid Mode since: a celebration
  // of Oinks that have just been taken back would be worse than no moment at all.
  const pg = to.pendingOinkGift;
  if (pg && pg.amount === row.amount && (sibling ? (!sender || pg.from === sender.name) : pg.from === null)) delete to.pendingOinkGift;
  row.reversed = true;
  s.transactions.unshift({ id: newId(), deviceId, childId: to.id, amount: taken, reason: row.reason, type: "debit", balance: "oinks", kind: row.kind, timestamp: nowIso, reversalOf: row.id });
  if (sender) {
    ensureCosmetics(sender);
    sender.oinks += taken;
    // The sender's lists had no row for the gift (reported under LB-356, unchanged); they get
    // one for the return, marked as a reversal, so the Oinks arriving back are explained.
    s.transactions.unshift({ id: newId(), deviceId, childId: sender.id, amount: taken, reason: row.reason, type: "credit", balance: "oinks", kind: row.kind, timestamp: nowIso, reversalOf: row.id });
  }
  return taken;
}
